/**
 * engine/remuneracao.ts — a REMUNERAÇÃO DAS UNIDADES (linha "Receitas Financeiras" do Fluxo mensal),
 * pelo "Memorial de Cálculo — Remuneração das unidades FinCast" (16/10/2026).
 *
 * O rendimento que os valores financiados de cada unidade geram RETIDOS na Caixa, da assinatura até o
 * registro, em duas partes:
 *   - terreno: a fração de terreno × fator (Poupança + TR) da assinatura até o registro (memorial, 2);
 *   - obra: em cada FASE entre medições, saldo de financiamento × medição acumulada no início da fase ×
 *     fator da fase (memorial, 3).
 * Juros SIMPLES: taxa do dia = (Poupança do mês + TR do mês) ÷ 30; fator de [a, b) = soma das taxas do
 * dia, de a (incluído) até b (excluído) (memorial, 1). IR pela tabela regressiva, uma alíquota por
 * unidade pelo prazo da assinatura até o registro (memorial, 4). Tudo é lançado no REGISTRO (memorial, 5).
 *
 * As séries (decisão de 16/10/2026, "Trocar para a 204 nos juros também"): Poupança = SGS 204 (só a
 * remuneração adicional — 0,5% a.m. ou 70% da Selic; a 196 já inclui a TR e a contaria duas vezes) e
 * TR = SGS 7811 (a TR do 1º dia do mês = a 226 no dia 1). As duas são DIÁRIAS no Banco Central (por
 * data de aniversário); o Fincast guarda o valor do dia 1 de cada mês — a taxa vale o mês civil inteiro
 * (simplificação S1, granularidade mensal, D1).
 *
 * Lacunas (memorial, 1): mês sem valor usa o mais recente anterior; antes da primeira taxa, a primeira;
 * depois da última, a última. A tabela de projeção de taxas NÃO é usada aqui.
 *
 * Puro: sem relógio, sem rede.
 */

import { addDays, primeiroDia, ymOf } from './datas';
import type { TaxasMensais } from './juros';
import type { IsoDate, Ym } from './tipos';

/** dias corridos de `a` até `b` (b − a), em UTC — o mesmo calendário de `addDays` */
export function diasEntre(a: IsoDate, b: IsoDate): number {
  const t = (d: IsoDate) => { const [y, m, dia] = String(d).split('-').map(Number); return Date.UTC(y!, m! - 1, dia!); };
  return Math.round((t(b) - t(a)) / 86_400_000);
}

/** Memorial, 4: a alíquota de IR pelo prazo em dias corridos da assinatura até o registro. */
export function aliquotaIR(dias: number): number {
  if (dias <= 180) return 0.225;
  if (dias <= 360) return 0.20;
  if (dias <= 720) return 0.175;
  return 0.15;
}

/** '' = calculado; 'sem-taxas' = sem série de Poupança ou de TR (a remuneração fica zerada);
 *  'historico-incompleto' = uma das séries começa depois da primeira assinatura (memorial, 1) */
export type AvisoRemuneracao = '' | 'sem-taxas' | 'historico-incompleto';

export interface TaxaRemuneracao {
  /** (Poupança + TR) do mês, em FRAÇÃO no mês (0,007 = 0,7% a.m.) */
  taxaDoMes: (ym: Ym) => number;
  /** o primeiro mês em que as DUAS séries têm valor; `null` sem séries */
  inicio: Ym | null;
  semTaxas: boolean;
}

/** As taxas da remuneração a partir das séries realizadas do Banco Central (`taxas.realizado`). */
export function taxaRemuneracao(taxas: TaxasMensais | null | undefined): TaxaRemuneracao {
  const real = taxas?.realizado ?? {};
  const serie = (campo: 'poupMes' | 'trMes') => Object.keys(real).map(Number)
    .filter(k => typeof real[k]?.[campo] === 'number' && Number.isFinite(real[k]![campo]!))
    .sort((a, b) => a - b);
  const poup = serie('poupMes'), tr = serie('trMes');
  const semTaxas = !poup.length || !tr.length;
  /** o valor do mês: o próprio; senão o mais recente anterior; antes do primeiro, o primeiro */
  const valor = (meses: number[], campo: 'poupMes' | 'trMes', ym: Ym) => {
    let k = meses[0]!;
    for (const x of meses) { if (x <= ym) k = x; else break; }
    return real[k]![campo]! / 100;
  };
  return {
    taxaDoMes: (ym: Ym) => (semTaxas ? 0 : valor(poup, 'poupMes', ym) + valor(tr, 'trMes', ym)),
    inicio: semTaxas ? null : Math.max(poup[0]!, tr[0]!),
    semTaxas,
  };
}

/** Memorial, 1: o fator de [de, ate) — soma das taxas do dia (taxa do mês ÷ 30), juros simples. */
export function fatorPoupancaTR(de: IsoDate, ate: IsoDate, taxaDoMes: (ym: Ym) => number): number {
  let f = 0, cur = de;
  while (cur < ate) {
    const ym = ymOf(cur)!;
    const fim = primeiroDia(ym + 1);
    const seg = fim < ate ? fim : ate;
    f += diasEntre(cur, seg) * taxaDoMes(ym) / 30;
    cur = seg;
  }
  return f;
}

/** Uma medição: a partir de `data` (inclusive), a medição acumulada é `ac` (0..1). */
export interface MedicaoDatada { data: IsoDate; ac: number }

export interface FaseRemuneracao {
  de: IsoDate;
  ate: IsoDate;
  dias: number;
  /** medição acumulada no início da fase (já com a medição do próprio dia) */
  ac: number;
  base: number;
  fator: number;
  valor: number;
}

export interface RemuneracaoUnidade {
  /** prazo em dias corridos da assinatura até o registro */
  dias: number;
  aliquota: number;
  /** fator (Poupança + TR) da assinatura até o registro */
  fator: number;
  /** remuneração BRUTA do terreno e da obra */
  terreno: number;
  obra: number;
  bruta: number;
  ir: number;
  terrenoLiq: number;
  obraLiq: number;
  liquida: number;
  fases: FaseRemuneracao[];
}

/**
 * A remuneração de UMA unidade (ou lote), lançada no registro.
 *
 * @param medicoes  as medições datadas (em qualquer ordem); só as com assinatura < data < registro abrem fase
 * @param acInicial a medição acumulada antes da primeira medição da lista (no Fincast, o PLS0)
 */
export function remuneracaoDaUnidade(
  u: { terreno: number; saldo: number; dataAssinatura: IsoDate; dataRegistro: IsoDate },
  medicoes: readonly MedicaoDatada[],
  acInicial: number,
  taxaDoMes: (ym: Ym) => number,
): RemuneracaoUnidade {
  const ini = u.dataAssinatura, fim = u.dataRegistro;
  const dias = Math.max(0, diasEntre(ini, fim));
  const aliquota = aliquotaIR(dias);
  const ord = [...medicoes].sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : 0));
  /** memorial, 3: "O início da fase já inclui a medição do próprio dia" */
  const acEm = (d: IsoDate) => { let ac = acInicial; for (const x of ord) { if (x.data <= d) ac = x.ac; else break; } return ac; };
  const fator = fim > ini ? fatorPoupancaTR(ini, fim, taxaDoMes) : 0;
  const terreno = (u.terreno || 0) * fator;
  const fases: FaseRemuneracao[] = [];
  if (fim > ini) {
    const pontos = [ini, ...[...new Set(ord.map(x => x.data))].filter(d => d > ini && d < fim), fim];
    for (let k = 0; k + 1 < pontos.length; k++) {
      const de = pontos[k]!, ate = pontos[k + 1]!;
      const ac = acEm(de), base = (u.saldo || 0) * ac, f = fatorPoupancaTR(de, ate, taxaDoMes);
      fases.push({ de, ate, dias: diasEntre(de, ate), ac, base, fator: f, valor: base * f });
    }
  }
  const obra = fases.reduce((s, x) => s + x.valor, 0);
  const bruta = terreno + obra;
  return {
    dias, aliquota, fator, terreno, obra, bruta, ir: bruta * aliquota,
    terrenoLiq: terreno * (1 - aliquota), obraLiq: obra * (1 - aliquota), liquida: bruta * (1 - aliquota), fases,
  };
}

/** As medições do fluxo como datas: a do mês `ym[m]` no último dia dele (P4), com a acumulada `medAc[m]`. */
export function medicoesDoFluxo(ym: readonly Ym[], medAc: readonly number[]): MedicaoDatada[] {
  return ym.map((y, m) => ({ data: addDays(primeiroDia(y + 1), -1), ac: medAc[m]! }));
}
